import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FormStateChangeProps } from '~formsShared/types';
import { Company } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import {
  CompanyForm,
  CompanyFormHandler,
  CompanyFormValues,
} from '~pages/Admin/Companies/CompanyForm';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

export const GeneralSettings = observer(() => {
  const [currentCompany, setCurrentCompany] = useState<Nullable<Company>>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState<Nullable<string>>(null);

  const [currentFormDirty, setCurrentFormDirty] = useState<boolean>(false);
  const { updateCompany, uploadLogo, deleteLogo, getCompanyById } = useCompany();
  const { toasterStore, userStore } = useStores();
  const companyActionsRef = useRef<CompanyFormHandler>(null);
  const companyId = userStore.currentCompanies[0]?.id || userStore.userCompany?.id;

  const onSuccess = useCallback((company?: Company) => {
    toasterStore.push(
      alert(
        t('company.company_updated', { name: company?.legalName }),
        AlertTypes.success,
      ),
    );
  }, []);

  // Logo dirtiness is CompanyForm's to report (through onFormStateChange, which
  // folds staged logo changes into isDirty): forcing currentFormDirty here as
  // well raced the undo path's not-dirty reset — the delete round-trip resolved
  // after the reset and re-enabled Update for a change the user had reverted.
  const onLogoMutationSuccess = useCallback((company: Company, messageKey: string) => {
    setLogoUrl(company.logoUrl);
    toasterStore.push(alert(t(messageKey), AlertTypes.success));
  }, []);

  const onCancel = () => {
    setIsEditing(false);
    setCurrentFormDirty(false);
    companyActionsRef.current?.resetForm();
  };

  const onChangeLogo = (file: Nullable<File>) => {
    if (file && companyId) {
      uploadCompanyLogo(companyId, file);
    }
  };

  const uploadCompanyLogo = useCallback(
    async (id: string, logo: File) => {
      setIsUploadingLogo(true);
      try {
        await uploadLogo({
          id: id,
          file: logo,
          callBack: (company: Company) =>
            onLogoMutationSuccess(company, 'company.logo_uploaded'),
        });
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [onLogoMutationSuccess, uploadLogo],
  );

  const onRemoveLogo = useCallback(async () => {
    if (companyId) {
      setIsUploadingLogo(true);
      try {
        const company = await deleteLogo({ id: companyId });
        if (company) {
          onLogoMutationSuccess(company, 'company.logo_deleted');
        }
      } finally {
        setIsUploadingLogo(false);
      }
    }
  }, [companyId, deleteLogo, onLogoMutationSuccess]);

  const onSubmitCallback = useCallback(
    async (data: CompanyFormValues) => {
      setIsUpdatingCompany(true);
      try {
        const updatedCompany = await updateCompany({ company: data });
        if (updatedCompany) {
          setCurrentCompany(updatedCompany);
        }
        onSuccess(updatedCompany);
        setIsEditing(false);
      } finally {
        setIsUpdatingCompany(false);
      }
    },
    [onSuccess, updateCompany],
  );

  const onFormStateChange = ({ isDirty }: FormStateChangeProps) => {
    setCurrentFormDirty(isDirty);
  };

  const onSubmitForm = () => {
    companyActionsRef.current?.submit(onSubmitCallback);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      if (!companyId) {
        return;
      }

      setIsCompanyLoading(true);
      try {
        const company = await getCompanyById({ id: companyId });
        setCurrentCompany(company ?? null);
      } finally {
        setIsCompanyLoading(false);
      }
    };

    fetchCompany();
  }, [companyId]);

  useEffect(() => {
    if (currentCompany) {
      setLogoUrl(currentCompany.logoUrl);
      companyActionsRef.current?.resetForm();
    }
  }, [currentCompany]);

  const isBusy = isUploadingLogo || isUpdatingCompany || isCompanyLoading;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        maxWidth: '800px',
      }}
    >
      <SettingsPageHeader
        title={t('page_headings.general_settings')}
        description={t('settings.page_descriptions.general_settings')}
      />
      <Box
        className="sticky-top"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          py: 2,
          mt: -2,
          mb: -4,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            gap: 1,
          }}
        >
          {isEditing ? (
            <SmallButton onClick={onCancel} color="secondary" variant="outlined">
              {t('actions.cancel')}
            </SmallButton>
          ) : (
            <SmallButton
              color="brandV2Yellow"
              disabled={isBusy}
              onClick={() => setIsEditing((prev) => !prev)}
            >
              {t('actions.edit')}
            </SmallButton>
          )}

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={!isEditing || !currentFormDirty || isBusy}
            loading={isUpdatingCompany}
            onClick={onSubmitForm}
          >
            {t('actions.update')}
          </SmallLoadingButton>
        </Box>
      </Box>
      {currentCompany?.id && (
        <CompanyForm
          ref={companyActionsRef}
          editable={isEditing}
          defaultCompany={currentCompany}
          logoUrl={logoUrl}
          onFormStateChange={onFormStateChange}
          onChangeLogo={onChangeLogo}
          onRemoveLogo={onRemoveLogo}
        />
      )}
    </Box>
  );
});
